// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="./window.types.d.ts" />
import { Injectable } from '@angular/core';
import { fromEvent, Observable } from 'rxjs';

/**
 * Service containing proxy methods and properties for Window
 * Note that some getters are only to be used from within an app or native/web container.
 * For instance: use `isMobile()` method to determine environment, not the `mobile` property.
 */
@Injectable({
    providedIn: 'root'
})
export class ApWindowService {
    /**
     * Only mobile devices support a native app
     */
    public supportsNativeApp(): boolean {
        return this.isMobile;
    }

    /**
     * Test if isMobile by checking userAgent
     */
    public get isMobile(): boolean {
        return this.isAndroid || this.isIOS;
    }

    /**
     * Test if isIOS by checking userAgent
     */
    public get isIOS(): boolean {
        // Older iPads pretend to be desktop, so we have to check touch points (but navigator.platform is deprecated)
        return (
            this.testUserAgent(/iphone|ipad/) ||
            (this.testUserAgent(/macintosh/) && window.navigator?.maxTouchPoints > 1 && !window.hasOwnProperty('MSStream'))
        );
    }

    /**
     * Test if isAndroid by checking userAgent
     */
    public get isAndroid(): boolean {
        return this.testUserAgent(/android/);
    }

    /**
     * Reference to mobile globalThis.
     * *Only works in native/web container*
     */
    public get mobile(): Window | undefined {
        return this.ios || this.android;
    }

    /**
     * Reference to Android globalThis.
     * *Only works in native/web container*
     */
    public get android(): Window | undefined {
        // @ts-ignore
        return window.android;
    }

    /**
     * Reference to iOS globalThis.
     * *Only works in native/web container*
     */
    public get ios(): Window | undefined {
        // @ts-ignore
        return window.webkit?.messageHandlers.ios ? window.webkit.messageHandlers.ios : undefined;
    }

    /**
     * Cordova is still used by external applications, mainly ios
     */
    public isInAppBrowser(): boolean {
        const userAgent = window.navigator.userAgent.toLowerCase();
        return userAgent.indexOf('cordova') > -1;
    }

    /**
     * returns browser `window` object. This method exists such that it can be mocked in unit tests.
     */
    public getWindow(): Window {
        return window;
    }

    /**
     * returns `window.location.hostname`
     */
    public location_hostname(): string {
        return window.location.hostname;
    }

    /**
     * returns `window.location.protocol`
     *
     */
    public location_protocol(): string {
        return window.location.protocol;
    }

    /**
     * without param returns de current href
     * with param the href will be set to the given value
     */
    public location_href(href?: any): string | void {
        if (!href) {
            return window.location.href;
        }
        window.location.href = href;
    }

    /**
     * Proxy to `window.location.reload()`
     */
    public location_reload(): void {
        window.location.reload();
    }

    /**
     * Proxy to `window.open(url)`
     */
    public open(url: string): void {
        window.open(url);
    }

    /**
     * Proxy to `window.innerHeight`
     */
    public innerHeight(): number {
        return window.innerHeight;
    }

    /**
     * Proxy to `window.innerWidth`
     */
    public innerWidth(): number {
        return window.innerWidth;
    }

    /**
     * Proxy to `window.scrollY`
     */
    public pageYOffset(): number {
        return window.scrollY;
    }

    /**
     * Proxy to `window.postMessage(payload, '*')`
     */
    public postMessage(payload: any): void {
        window.postMessage(payload, '*');
    }

    /**
     * Proxy to `window.parent.postMessage(payload, '*')`
     */
    public parentPostMessage(payload: any): void {
        window.parent.postMessage(payload, '*');
    }

    /**
     * Proxy to mobile `postMessage`
     */
    public mobilePostMessage(payload: any): void {
        this.mobile?.postMessage(payload);
    }

    /**
     * Proxy to `window.screen.availWidth`
     */
    public availWidth(): number {
        return window.screen.availWidth;
    }

    /**
     * Proxy to `window.screen.availHeight`
     */
    public availHeight(): number {
        return window.screen.availHeight;
    }

    /**
     * Proxy to `window.screen.colorDepth`
     */
    public screenColorDepth(): number {
        return window.screen.colorDepth;
    }

    /**
     * Proxy to `window.screen.height`
     */
    public screenHeight(): number {
        return window.screen.height;
    }

    /**
     * Proxy to `window.screen.width`
     */
    public screenWidth(): number {
        return window.screen.width;
    }

    /**
     * Receive message by `fromEvent` on `window`
     */
    public receiveMessage(): Observable<Event> {
        return fromEvent(window, 'message');
    }

    private testUserAgent(reg: RegExp): boolean {
        return reg.test(window.navigator.userAgent.toLowerCase());
    }
}
